import { observable, computed, action } from 'mobx'

import { Filters } from 'utils'

class AudienceFiltersStore {
  filterGroup = 'audienceFilters'

  @observable
  audienceGenders = Filters.getDefaultFilter({ group: this.filterGroup, filter: 'audienceGenders' })

  @observable
  audienceAges = Filters.getDefaultFilter({ group: this.filterGroup, filter: 'audienceAges' })

  @observable
  audienceCountries = Filters.getDefaultFilter({ group: this.filterGroup, filter: 'audienceCountries' })

  @computed
  get audienceFilters() {
    return {
      audienceGenders: this.audienceGenders,
      audienceAges: this.audienceAges,
      audienceCountries: this.audienceCountries,
    }
  }

  audienceFilterKeys = ['audienceGenders', 'audienceAges', 'audienceCountries']

  @action
  changeAudienceFilter(filter, value) {
    this[filter] = value
  }

  @action
  setAudienceFilters({ audienceGenders, audienceAges, audienceCountries }) {
    if (audienceGenders) this.audienceGenders = audienceGenders
    if (audienceAges) this.audienceAges = audienceAges
    if (audienceCountries) this.audienceCountries = audienceCountries
  }

  @action resetAudienceFilter() {
    this.audienceGenders = Filters.getDefaultFilter({ group: this.filterGroup, filter: 'audienceGenders' })
    this.audienceAges = Filters.getDefaultFilter({ group: this.filterGroup, filter: 'audienceAges' })
    this.audienceCountries = Filters.getDefaultFilter({ group: this.filterGroup, filter: 'audienceCountries' })

    return {
      audienceGenders: this.audienceGenders,
      audienceAges: this.audienceAges,
      audienceCountries: this.audienceCountries,
    }
  }

  @action getAudienceLabels() {
    return {
      audienceGenders: '',
      audienceAges: '',
      audienceCountries: '',
    }
  }

  @action getAudienceVariables({
    countryKey = 'country',
    agesKey = 'ages',
    gendersKey = 'genders',
    omitVariableKeys = true,
    newCountryType = false,
  } = {}) {
    const ages = Array.isArray(this.audienceAges)
      ? this.audienceAges.map(f => f.value).filter(Boolean)
      : this.audienceAges.value || []
    const genders = Array.isArray(this.audienceGenders)
      ? this.audienceGenders.map(f => f.value).filter(Boolean)
      : this.audienceGenders.value || []

    // some graphQL queries (e.g. topics query) enforces the client to send an audience object with all the keys
    // while in other queries (most of the cases) it is better to omit those keys.
    if (!omitVariableKeys) {
      return {
        [gendersKey]: genders,
        [agesKey]: ages,
        [countryKey]: this.audienceCountries.value || [],
      }
    }

    return {
      ...((genders.length || genders.value) && { [gendersKey]: genders }),
      ...((ages.length || ages.value) && { [agesKey]: ages }),
      ...((this.audienceCountries.length || this.audienceCountries.value) && {
        [countryKey]: this.audienceCountries.value || [],
        ...(newCountryType && { country: this.audienceCountries.value || '' }),
      }),
    }
  }

  getLabels = filter => (Array.isArray(filter) ? filter.map(f => f.label).join(', ') : filter.label)

  @action getAudienceDescription() {
    return `${this.getLabels(this.audienceCountries)}, ${this.getLabels(this.audienceGenders)}, ${this.getLabels(
      this.audienceAges
    )}`
  }

  @action getActiveAudienceDescription() {
    const audienceObj = this.getAudienceVariables()

    if (Object.keys(audienceObj).length === 0) return 'All'

    const activeAudienceFiltersLabels = Object.keys(audienceObj).map(audienceFilterKey => {
      switch (audienceFilterKey) {
        case 'genders':
          return this.getLabels(this.audienceGenders)
        case 'ages':
          return this.getLabels(this.audienceAges)
        case 'country':
          return this.getLabels(this.audienceCountries)
        default:
          return null
      }
    })

    return activeAudienceFiltersLabels.join()
  }
}

export default new AudienceFiltersStore()
